import { IS_AWS_ENV } from "constants/app.constants";
import styled from "styled-components";

import useProperty from "hooks/useProperty";

import { WellDetailCardData } from "api/data";

import { EntityKind } from "models/entityKind";

import { formatPanelValue, useIsFieldGroupEnabled } from "../PanelField";
import { LIFECYCLE_MILESTONES } from "../constants";
import { MISSING, MISSING_VALUE, SectionLabel, TEAL } from "../styles";

const DATES_GROUP = "Dates";

function Milestone({
  label,
  propertyName,
  wellData,
  fixedLabel,
  isFirst,
  isLast
}: {
  label: string;
  propertyName: Maybe<string>;
  fixedLabel: boolean;
  wellData: Maybe<WellDetailCardData>;
  isFirst: boolean;
  isLast: boolean;
}): JSX.Element {
  const property = useProperty(propertyName ?? "", false, "", EntityKind.Well);
  const raw = propertyName ? wellData?.[propertyName] : null;
  // A date the response reports as "n/a" is a milestone that has not happened,
  // so it reads as the missing state rather than filling the dot in.
  const date = formatPanelValue(raw);
  const complete = !!date;

  return (
    <MilestoneColumn>
      <MilestoneLabel>
        {fixedLabel ? label : property?.fullContextTitle ?? label}
      </MilestoneLabel>
      <DotRow>
        <Connector $hidden={isFirst} />
        <Dot $complete={complete} />
        <Connector $hidden={isLast} />
      </DotRow>
      <MilestoneDate $complete={complete}>{date ?? MISSING_VALUE}</MilestoneDate>
    </MilestoneColumn>
  );
}

export function LifecycleTimeline({
  wellData
}: {
  wellData: Maybe<WellDetailCardData>;
}): JSX.Element {
  const groupEnabled = useIsFieldGroupEnabled(DATES_GROUP);
  if (!groupEnabled) return null;

  return (
    <Wrapper>
      <SectionLabel $gap={10}>Lifecycle</SectionLabel>
      <Track>
        {LIFECYCLE_MILESTONES.map((milestone, index) => (
          <Milestone
            key={milestone.label}
            label={milestone.label}
            propertyName={milestone.property}
            fixedLabel={!!milestone.fixedLabel}
            wellData={wellData}
            isFirst={index === 0}
            isLast={index === LIFECYCLE_MILESTONES.length - 1}
          />
        ))}
      </Track>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  border-top: 1px solid #e3e6e8;
  background: #f9fafc;
  padding: 10px 14px 16px;
`;

const Track = styled.div`
  display: grid;
  grid-template-columns: repeat(${LIFECYCLE_MILESTONES.length}, 1fr);
`;

const MilestoneColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 0;
`;

// Every AWS label fits on one line even at the narrowest card, so it sits 6px
// above the dot row, matching the date's 6px below it. Azure keeps a two-line
// box for its longer catalogue titles.
const MilestoneLabel = styled.div`
  font-size: 12px;
  color: #3c4858;
  text-align: center;
  ${IS_AWS_ENV ? "padding-bottom: 6px;" : "height: 34px;"}
  line-height: 1.3;
`;

const DotRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 14px;
`;

const Connector = styled.span<{ $hidden: boolean }>`
  flex: 1;
  height: 1px;
  background: ${(props) => (props.$hidden ? "transparent" : "#e3e6e8")};
`;

const Dot = styled.span<{ $complete: boolean }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex: none;
  background: ${(props) => (props.$complete ? TEAL : "#fff")};
  border: 2px solid ${(props) => (props.$complete ? TEAL : "#c0ccda")};
`;

const MilestoneDate = styled.div<{ $complete: boolean }>`
  font-family: var(--fontMono);
  font-size: 12px;
  margin-top: 6px;
  color: ${(props) => (props.$complete ? "var(--color-text)" : MISSING)};
`;
